import md5 from "md5";
import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import FilterPanel, {
  FilterOption,
} from "../../../component/FilterPanel/FilterPanel";
import request from "../../../api/request";
import { User } from "../../../const/model";
import { UserRole } from "../../../const/enum";
import { BackendAPI } from "../../../const/api";
import { getLastSyncTime } from "../../../services";
import { useToast } from "../../../component/ToastBar";
import { AuthContext } from "../../../auth/authContext";
import { useNavigate } from "react-router-dom";
import { Dialogs } from "../../../component/Dialogs";
import ResetPasswordDialog from "../../../component/ResetPasswordDialog";

interface Props {
  type: "customer" | "staff";
}

const UserList = (props: Props) => {
  const toast = useToast();
  const { type } = props;
  const [users, setUsers] = useState<User[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const [lastSyncTime, setLastSyncTime] = useState("");
  const [syncRes, setSyncRes] = useState<{
    created: number;
    updated: number;
  } | null>(null);
  const [openBanDialog, setOpenBanDialog] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deletingUsername, setDeletingUsername] = useState<string | null>(null);
  const [deletingUserDisabled, setDeletingUserDisabled] = useState<boolean>(false);
  const [openResetPasswordDialog, setOpenResetPasswordDialog] = useState(false);
  const [resettingUserId, setResettingUserId] = useState<string | null>(null);
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (!authContext) {
      navigate("/dashboard/orders");
      return;
    }
    if (type === "staff" && !authContext.isAdmin()) {
      navigate("/dashboard/orders");
    }
  }, [authContext, navigate, type]);

  const fetchLastSyncTime = async () => {
    const lastSyncTime = await getLastSyncTime("customer");
    setLastSyncTime(lastSyncTime);
  };

  useEffect(() => {
    fetchLastSyncTime().then();
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const params = {
        page: page + 1, // API可能期望从1开始的页码
        size: rowsPerPage,
        sort: sortConfig.key ? `${sortConfig.key},${sortConfig.direction}` : "",
        ...filters,
      };
      const response = await request.get(
        type === "customer" ? BackendAPI.customerList : BackendAPI.staffList,
        params,
      );
      if (!response.success) {
        return;
      }
      setUsers(response.data.users);
      setTotalCount(response.data.total);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }, [filters, page, rowsPerPage, sortConfig.direction, sortConfig.key, type]);

  useEffect(() => {
    fetchUsers().then();
    // eslint-disable-next-line
  }, [type, page, rowsPerPage, filters]);

  useEffect(() => {
    // sort materials by sortConfig
    const sortedUsers = users.sort((a, b) => {
      if (sortConfig.direction === "asc") {
        return a[sortConfig.key] > b[sortConfig.key] ? 1 : -1;
      } else {
        return a[sortConfig.key] < b[sortConfig.key] ? 1 : -1;
      }
    });
    setUsers(sortedUsers);
    // eslint-disable-next-line
  }, [sortConfig]);

  // 处理筛选变化
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPage(0);
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const handleSync = async () => {
    request.get(BackendAPI.syncCustomerList).then((res) => {
      if (res.success) {
        const data = res.data;
        setSyncRes({
          created: data.created,
          updated: data.updated,
        });
        toast("同步成功");
        fetchLastSyncTime().then();
        fetchUsers().then();
      }
    });
  };

  const handleBanUser = async () => {
    if (!deletingId) {
      return;
    }
    
    // 对于客户列表，需要使用user id而不是id
    const targetUser = users.find(u => u.id === deletingId);
    const userId = type === "customer" ? targetUser?.userId : deletingId;
    
    if (!userId) {
      toast("无法找到用户ID", "error");
      return;
    }
    
    const isCurrentlyDisabled = targetUser?.deleted;
    const action = isCurrentlyDisabled ? "启用" : "禁用";
    
    const res = await request.post(BackendAPI.banUser, { userId: userId });
    if (res.success) {
      toast(res.data?.message || `${action}成功`);
      setOpenBanDialog(false);
      fetchUsers().then();
    } else {
      toast(res.message, "error");
    }
    setDeletingId(null);
    setDeletingUsername(null);
  };

  const handleResetPassword = async (newPassword: string) => {
    if (!resettingUserId) {
      return;
    }
    
    // 对于客户列表，需要使用user id而不是id
    const targetUser = users.find(u => u.id === resettingUserId);
    const userId = type === "customer" ? targetUser?.userId : resettingUserId;
    
    if (!userId) {
      toast("无法找到用户ID", "error");
      return;
    }
    
    const res = await request.post(BackendAPI.resetUserPassword, { 
      user_id: userId,
      new_password: md5(newPassword)
    });
    if (res.success) {
      toast("重置密码成功");
      setResettingUserId(null);
      setOpenResetPasswordDialog(false);
      fetchUsers().then();
    } else {
      toast(res.message, "error");
    }
  };

  const tables = () => {
    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableCell onClick={() => handleSort("name")}>用户名</TableCell>
            {type === "customer" && (
              <TableCell onClick={() => handleSort("code")}>用户编码</TableCell>
            )}
            {type === "customer" && (
              <TableCell onClick={() => handleSort("abbreviation")}>
                简称
              </TableCell>
            )}
            {type === "customer" && (
              <TableCell onClick={() => handleSort("salesperson")}>
                业务员
              </TableCell>
            )}
            {type === "customer" && (
              <TableCell onClick={() => handleSort("group")}>分组</TableCell>
            )}
            {type === "customer" && (
              <TableCell onClick={() => handleSort("organization")}>
                使用组织
              </TableCell>
            )}
            {type === "customer" && (
              <TableCell onClick={() => handleSort("address")}>地址</TableCell>
            )}
            {type === "customer" && (
              <TableCell onClick={() => handleSort("creator")}>
                创建人
              </TableCell>
            )}
            {type === "customer" && (
              <TableCell onClick={() => handleSort("paymentTerms")}>
                付款方式
              </TableCell>
            )}
            {type === "staff" && (
              <TableCell onClick={() => handleSort("role")}>角色</TableCell>
            )}
            {type === "staff" && (
              <TableCell onClick={() => handleSort("createdAt")}>
                创建时间
              </TableCell>
            )}
            {type === "customer" && (
              <TableCell onClick={() => handleSort("auditor")}>
                审核人
              </TableCell>
            )}
            {type === "customer" && (
              <TableCell onClick={() => handleSort("auditAt")}>
                审核日期
              </TableCell>
            )}
            {type === "customer" && (
              <TableCell onClick={() => handleSort("creditManagement")}>
                信用管理
              </TableCell>
            )}
            {authContext && authContext.isAdmin() && (
              <TableCell>操作</TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user) => {
            return (
              <TableRow key={user.id}>
                <TableCell>{user.name}</TableCell>
                {type === "customer" && <TableCell>{user.code}</TableCell>}
                {type === "customer" && (
                  <TableCell>{user.abbreviation}</TableCell>
                )}
                {type === "customer" && (
                  <TableCell>{user.salesperson}</TableCell>
                )}
                {type === "customer" && <TableCell>{user.group}</TableCell>}
                {type === "customer" && (
                  <TableCell>{user.organization}</TableCell>
                )}
                {type === "customer" && <TableCell>{user.address}</TableCell>}
                {type === "customer" && <TableCell>{user.creator}</TableCell>}
                {type === "customer" && (
                  <TableCell>{user.paymentTerms}</TableCell>
                )}
                {type === "staff" && (
                  <TableCell>{UserRole[user.role]}</TableCell>
                )}
                {type === "staff" && <TableCell>{user.createdAt}</TableCell>}
                {type === "customer" && <TableCell>{user.auditor}</TableCell>}
                {type === "customer" && <TableCell>{user.auditAt}</TableCell>}
                {type === "customer" && (
                  <TableCell>{user.creditManagement ? "是" : "否"}</TableCell>
                )}
                {authContext && authContext.isAdmin() && (
                  <TableCell>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <Button
                        onClick={() => {
                          setOpenResetPasswordDialog(true);
                          setResettingUserId(user.id);
                        }}
                        variant="outlined"
                        size="small"
                        color="primary"
                      >
                        重置密码
                      </Button>
                      <Button
                        onClick={() => {
                          setOpenBanDialog(true);
                          setDeletingId(user.id);
                          setDeletingUsername(user.name);
                          setDeletingUserDisabled(user.deleted || false);
                        }}
                        variant="outlined"
                        size="small"
                        color={user.deleted ? "success" : "error"}
                      >
                        {user.deleted ? "启用" : "禁用"}
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  const filterOptions = (): FilterOption[] => {
    const options: FilterOption[] = [{ key: "name", label: "名称", type: "text" }];
    if (type === "customer") {
      options.push({ key: "code", label: "用户编码", type: "text" });
    }
    options.push({ 
      key: "deleted", 
      label: "禁用状态", 
      type: "select",
      options: [
        {
          label: "全部",
          value: "",
        },
        {
          label: "启用",
          value: "false",
        },
        {
          label: "禁用",
          value: "true",
        },
      ]
    });
    return options;
  };

  return (
    <Paper>
      <FilterPanel
        filterOptions={filterOptions() as FilterOption[]}
        onFilterChange={handleFilterChange}
        needDate={false}
      />

      {type === "customer" && (
        <div>
          <Button onClick={handleSync}>同步金蝶客户列表</Button>
          {syncRes && (
            <span>
              =====本次客户：创建{`${syncRes.created}`}条, 更新
              {`${syncRes.updated}`}条{"   "}
            </span>
          )}
          <div />
          {lastSyncTime ? (
            <span>上次同步时间: {lastSyncTime}</span>
          ) : (
            <span>上次同步时间: 从未同步</span>
          )}
        </div>
      )}
      {tables()}
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) =>
          setRowsPerPage(parseInt(event.target.value, 10))
        }
      />
      <Dialogs
        open={openBanDialog}
        title={`是否要${deletingUserDisabled ? "启用" : "禁用"}${deletingUsername}`}
        content={deletingUserDisabled ? "启用后用户将可以登录" : "禁用后用户将无法登录"}
        onConfirm={handleBanUser}
        onCancel={() => {
          setDeletingId(null);
          setDeletingUsername(null);
          setDeletingUserDisabled(false);
          setOpenBanDialog(false);
        }}
      />
      <ResetPasswordDialog
        open={openResetPasswordDialog}
        username={users.find(u => u.id === resettingUserId)?.name || ""}
        onConfirm={handleResetPassword}
        onCancel={() => {
          setResettingUserId(null);
          setOpenResetPasswordDialog(false);
        }}
      />
    </Paper>
  );
};

export default UserList;
